import { uiElementPaddingStyle } from "./styles.mjs";
import { Text } from "./Text.mjs";
import { useTheme } from "./ThemeProvider.mjs";
import { useStyles } from "purse-styles";
import React, { useCallback, useRef, useState } from "react";
import { Dialog, DialogTrigger, Popover, Pressable } from "react-aria-components";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/TextArea.tsx
const TextArea = React.forwardRef(({ value, defaultValue, onChange, placeholder, style: style$1, className, autoFocus, onKeyDown, "aria-label": ariaLabel, size = "md" }, ref) => {
	const theme = useTheme();
	return /* @__PURE__ */ jsx("textarea", {
		ref,
		value,
		defaultValue,
		onChange: (e) => onChange?.(e.target.value),
		className: `${useStyles({
			fontFamily: theme.fontFamilies.Inter,
			...theme.fontStyles[size],
			resize: "none",
			border: "none",
			outline: "none",
			width: "100%",
			minHeight: "80px",
			...style$1
		})} ${className || ""}`,
		placeholder,
		autoFocus,
		onKeyDown,
		"aria-label": ariaLabel
	});
});
function useMeasure() {
	const [dimensions, setDimensions] = useState({
		width: null,
		height: null
	});
	const previousObserver = useRef(null);
	return [useCallback((node) => {
		if (previousObserver.current) {
			previousObserver.current.disconnect();
			previousObserver.current = null;
		}
		if (node?.nodeType === Node.ELEMENT_NODE) {
			const observer = new ResizeObserver(([entry]) => {
				if (entry && entry.borderBoxSize && entry.borderBoxSize[0]) {
					const { inlineSize: width, blockSize: height } = entry.borderBoxSize[0];
					setDimensions({
						width,
						height
					});
				}
			});
			observer.observe(node);
			previousObserver.current = observer;
		}
	}, []), dimensions];
}
function PopoverTextArea({ value, onSave, placeholder = "", style: style$1, size = "sm" }) {
	const theme = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const editorRef = useRef(null);
	const [containerRef, dimensions] = useMeasure();
	const hoverStyles = useStyles(uiElementPaddingStyle({ size }), {
		borderRadius: theme.borderRadiuses.md,
		"&:hover": { background: theme.colors.sand3 },
		cursor: "text"
	});
	const popoverStyles = useStyles({
		visibility: dimensions.width && dimensions.height ? "visible" : "hidden",
		position: "absolute",
		width: "var(--trigger-width)",
		background: theme.colors.sand1,
		borderRadius: theme.borderRadiuses.md,
		outline: `1px solid ${theme.colors.sandA3}`,
		minHeight: 80,
		boxShadow: theme.shadows.default,
		padding: 0
	});
	const handleSave = useCallback(() => {
		const newValue = editorRef.current?.value ?? value;
		if (newValue !== value) onSave(newValue);
		setIsOpen(false);
	}, [onSave, value]);
	const handleOpenChange = useCallback((open) => {
		if (!open) handleSave();
		else setIsOpen(open);
	}, [handleSave]);
	const textAreaPaddingStyles = useStyles(uiElementPaddingStyle({ size }));
	return /* @__PURE__ */ jsxs(DialogTrigger, {
		isOpen,
		onOpenChange: handleOpenChange,
		children: [/* @__PURE__ */ jsx(Pressable, { children: /* @__PURE__ */ jsx("div", {
			ref: containerRef,
			className: hoverStyles,
			style: {
				display: "flex",
				borderRadius: theme.borderRadiuses.md,
				...style$1
			},
			role: "button",
			"aria-label": placeholder,
			children: /* @__PURE__ */ jsx(Text, {
				size: "sm",
				light: !value,
				truncate: true,
				children: value || placeholder
			})
		}) }), /* @__PURE__ */ jsx(Popover, {
			offset: dimensions.height ? -dimensions.height : 0,
			placement: "bottom start",
			crossOffset: 0,
			className: popoverStyles,
			children: /* @__PURE__ */ jsx(Dialog, { children: /* @__PURE__ */ jsx(TextArea, {
				ref: editorRef,
				defaultValue: value,
				placeholder,
				size: "sm",
				style: {
					width: "100%",
					background: "transparent",
					minHeight: "100%",
					color: theme.colors.sand12
				},
				className: textAreaPaddingStyles,
				autoFocus: true,
				onKeyDown: (e) => {
					if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSave();
				},
				"aria-label": placeholder
			}) })
		})]
	});
}

//#endregion
export { PopoverTextArea, TextArea };